import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'

// ─── Tipos ────────────────────────────────────────────────────────────────

export interface CuentaPoint {
  id: string
  banco: string
  tipo: string
  numero: string
  titular: string
  identificacion_titular: string | null
  correo_confirmacion: string | null
  activo: boolean
  orden_visualizacion: number
  notas: string | null
  ultimos_4: string | null
  actualizado_en: string
}

export interface RazonSocial {
  id: number
  patron_matching: string
  razon_social_canonica: string
  ruc: string | null
  activo: boolean
  notas: string | null
  creado_en: string
}

export type VentanaFecha = 'hoy' | '7d' | 'mes_actual' | '30d' | 'custom'

export interface AntifraudeConfig {
  confianza_minima_ia: number       // 0.5..1.0
  ventana_fecha: VentanaFecha
  requiere_cliente_vinculado: boolean
  cross_check_saldo: boolean
  rechazar_manuscritos: boolean
  permitir_pagos_parciales: boolean
  actualizado_en: string
  actualizado_por: string | null
}

// ─── Queries ──────────────────────────────────────────────────────────────

export function useCuentasPoint() {
  return useQuery({
    queryKey: ['antifraude', 'cuentas'],
    queryFn: async (): Promise<CuentaPoint[]> => {
      const { data, error } = await supabase.rpc('wa_listar_cuentas_point')
      if (error) throw error
      return (data ?? []) as CuentaPoint[]
    },
    staleTime: 30_000,
  })
}

export function useRazonesPoint() {
  return useQuery({
    queryKey: ['antifraude', 'razones'],
    queryFn: async (): Promise<RazonSocial[]> => {
      const { data, error } = await supabase.rpc('wa_listar_razones_point')
      if (error) throw error
      return (data ?? []) as RazonSocial[]
    },
    staleTime: 30_000,
  })
}

export function useAntifraudeConfig() {
  return useQuery({
    queryKey: ['antifraude', 'config'],
    queryFn: async (): Promise<AntifraudeConfig | null> => {
      const { data, error } = await supabase.rpc('wa_leer_antifraude_config')
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : data
      return (row ?? null) as AntifraudeConfig | null
    },
    staleTime: 30_000,
  })
}

// ─── Mutations ────────────────────────────────────────────────────────────

export function useToggleCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: string; activo: boolean }) => {
      const { data, error } = await supabase.rpc('wa_toggle_cuenta_point', {
        id: args.id,
        activo: args.activo,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['antifraude', 'cuentas'] }),
  })
}

export function useUpsertCuenta() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: Omit<Partial<CuentaPoint>, 'id'> & { id?: string | null; banco: string; tipo: string; numero: string; titular: string }) => {
      const { data, error } = await supabase.rpc('wa_upsert_cuenta_point', {
        id: args.id ?? null,
        banco: args.banco,
        tipo: args.tipo,
        numero: args.numero,
        titular: args.titular,
        identificacion_titular: args.identificacion_titular ?? null,
        correo_confirmacion: args.correo_confirmacion ?? null,
        orden: args.orden_visualizacion ?? 999,
        ultimos_4: args.ultimos_4 ?? null,
        notas: args.notas ?? null,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['antifraude', 'cuentas'] }),
  })
}

export function useToggleRazon() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: number; activo: boolean }) => {
      const { data, error } = await supabase.rpc('wa_toggle_razon_point', {
        id: args.id,
        activo: args.activo,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['antifraude', 'razones'] }),
  })
}

export function useUpsertRazon() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id?: number | null; patron: string; canonica: string; ruc?: string | null; notas?: string | null }) => {
      const { data, error } = await supabase.rpc('wa_upsert_razon_point', {
        id: args.id ?? null,
        patron: args.patron,
        canonica: args.canonica,
        ruc: args.ruc ?? null,
        notas: args.notas ?? null,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['antifraude', 'razones'] }),
  })
}

export function useActualizarAntifraudeConfig() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: async (cambios: Partial<Omit<AntifraudeConfig, 'actualizado_en' | 'actualizado_por'>>) => {
      const { data, error } = await supabase.rpc('wa_actualizar_antifraude_config', {
        cambios,
        actualizado_por: user?.id ?? null,
      })
      if (error) throw error
      return data as { ok: boolean; cambios?: object; error?: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['antifraude', 'config'] }),
  })
}
